import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Download,
  Trash2,
  Phone,
  Mail,
  MapPin,
  MessageSquare,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRAND } from "@/config/brand";
import { TESTIDS } from "@/constants/testIds";

const STATUSES = ["all", "new", "contacted", "converted", "lost"];
const STATUS_COLORS = {
  new: "text-[color:var(--vs-blue)] border-[color:var(--vs-blue)]",
  contacted: "text-amber-400 border-amber-500/60",
  converted: "text-emerald-400 border-emerald-500/60",
  lost: "text-red-400 border-red-500/60",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const email = localStorage.getItem("vs_admin_email") || "admin";

  const fetchAll = async (status = filter) => {
    setLoading(true);
    try {
      const [ls, st] = await Promise.all([
        api.listLeads(status === "all" ? undefined : status),
        api.stats(),
      ]);
      setLeads(ls.items || []);
      setStats(st);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("vs_admin_token");
        nav("/admin/login");
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("vs_admin_token")) {
      nav("/admin/login");
      return;
    }
    fetchAll("all");
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchAll(filter);
    // eslint-disable-next-line
  }, [filter]);

  const logout = () => {
    localStorage.removeItem("vs_admin_token");
    localStorage.removeItem("vs_admin_email");
    nav("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.updateLeadStatus(id, status);
      toast.success("Status updated.");
      fetchAll(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.deleteLead(id);
      toast.success("Lead removed.");
      fetchAll(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed.");
    }
  };

  const exportCsv = () => {
    const headers = ["Date", "Name", "Phone", "Email", "Service", "Address", "Message", "Status"];
    const rows = leads.map((l) => [
      new Date(l.created_at).toISOString(),
      l.name,
      l.phone,
      l.email || "",
      l.service,
      l.address || "",
      (l.message || "").replace(/\n/g, " "),
      l.status,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vasquez-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalByStatus = stats.by_status || {};

  return (
    <div className="min-h-screen bg-[color:var(--vs-bg)] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Vasquez & Son" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display uppercase tracking-tight text-white font-semibold">
                Admin Console
              </div>
              <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-gray-500">
                Vasquez & Son · Leads
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:block font-mono-accent text-[11px] tracking-[0.2em] uppercase text-gray-400">
              {email}
            </span>
            <button
              onClick={logout}
              data-testid={TESTIDS.admin.logout}
              className="border-2 border-white/20 hover:border-white text-white px-4 py-2 font-display uppercase tracking-[0.15em] text-sm inline-flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <StatCard label="Total" value={stats.total || 0} accent />
          <StatCard label="New" value={totalByStatus.new || 0} />
          <StatCard label="Contacted" value={totalByStatus.contacted || 0} />
          <StatCard label="Converted" value={totalByStatus.converted || 0} />
          <StatCard label="Lost" value={totalByStatus.lost || 0} />
        </div>

        {/* Filter + export */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={
                  s === "all"
                    ? TESTIDS.admin.filterAll
                    : `admin-filter-${s}`
                }
                className={`px-4 py-2 font-display uppercase tracking-[0.15em] text-xs border-2 transition-colors ${
                  filter === s
                    ? "border-[color:var(--vs-blue)] bg-[color:var(--vs-blue)] text-white"
                    : "border-white/15 text-white/70 hover:border-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <button
            onClick={exportCsv}
            data-testid={TESTIDS.admin.export}
            className="btn-vs btn-secondary"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Leads table */}
        <div className="border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-black/60 text-left">
                <tr className="text-[10px] font-mono-accent tracking-[0.25em] uppercase text-white/50">
                  <th className="px-4 py-4">Date</th>
                  <th className="px-4 py-4">Name</th>
                  <th className="px-4 py-4">Contact</th>
                  <th className="px-4 py-4">Service</th>
                  <th className="px-4 py-4">Address / Notes</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center text-gray-500">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center text-gray-500">
                      No leads {filter !== "all" ? `with status "${filter}"` : "yet"}.
                    </td>
                  </tr>
                )}
                {!loading &&
                  leads.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`${TESTIDS.admin.leadRow}-${l.id}`}
                      className="hover:bg-white/5"
                    >
                      <td className="px-4 py-4 align-top whitespace-nowrap text-gray-400 font-mono-accent text-xs">
                        {new Date(l.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="text-white font-display uppercase tracking-tight text-base">
                          {l.name}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top space-y-1">
                        <a href={`tel:${l.phone}`} className="flex items-center gap-2 text-white/85 hover:text-[color:var(--vs-blue)]">
                          <Phone className="w-3.5 h-3.5" />
                          {l.phone}
                        </a>
                        {l.email && (
                          <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-white/60 hover:text-white text-xs">
                            <Mail className="w-3.5 h-3.5" />
                            {l.email}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <span className="inline-block border border-white/15 px-2.5 py-1 font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/80">
                          {l.service}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top max-w-sm">
                        {l.address && (
                          <div className="flex items-start gap-2 text-white/80 text-xs mb-1">
                            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>{l.address}</span>
                          </div>
                        )}
                        {l.message && (
                          <div className="flex items-start gap-2 text-white/60 text-xs">
                            <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span className="line-clamp-3">{l.message}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <select
                          value={l.status}
                          onChange={(e) => updateStatus(l.id, e.target.value)}
                          data-testid={`${TESTIDS.admin.statusSelect}-${l.id}`}
                          className={`bg-black px-3 py-1.5 border-2 font-mono-accent text-[11px] tracking-[0.2em] uppercase focus:outline-none ${
                            STATUS_COLORS[l.status] || "text-white border-white/20"
                          }`}
                        >
                          <option value="new">new</option>
                          <option value="contacted">contacted</option>
                          <option value="converted">converted</option>
                          <option value="lost">lost</option>
                        </select>
                      </td>
                      <td className="px-4 py-4 align-top text-right">
                        <button
                          onClick={() => remove(l.id)}
                          data-testid={`${TESTIDS.admin.delete}-${l.id}`}
                          className="inline-flex items-center gap-1 text-red-400/80 hover:text-red-400 font-mono-accent text-[11px] tracking-[0.2em] uppercase"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div
      className={`border p-5 md:p-6 ${
        accent
          ? "border-[color:var(--vs-blue)]/60 bg-[color:var(--vs-blue)]/10"
          : "border-white/10 bg-[#0f0f10]"
      }`}
    >
      <div className="text-3xl md:text-4xl font-bold chrome-text">
        <span className="font-mono-accent">{value}</span>
      </div>
      <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/50 mt-1">
        {label}
      </div>
    </div>
  );
}
